import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import DropDown from '_components/UI/DropDown';
import FacetRefinement from '@components/algolia/SearchResults/facets/FacetRefinement';
import { getFacetTitle } from '@components/algolia/SearchResults/facets/getFacetTitle';
import { useFilters } from '@components/algolia/SearchResults/FiltersProvider';

import styles from './FilterBar.module.scss';

/**
 * One of the first-8 facet pills in the PLP filter bar (ED-320). The pill is a
 * dropdown whose popover mounts the same refinement widget the all-filters
 * panel uses, so refining here and refining in the panel stay in sync through
 * the shared InstantSearch uiState.
 *
 * `hidden` pills (overflow that would push the "View filters" button to a
 * second line) stay mounted but invisible and out of flow, so the bar can
 * still measure them when space frees up.
 */
const FilterPill = ({
  attribute,
  hidden = false,
}: {
  attribute: string;
  hidden?: boolean;
}) => {
  const { t } = useTranslation(['searchResults']);
  const { locale, getRefinementCount, markFacetInteraction } = useFilters();
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  const markPinned = useCallback(
    () => markFacetInteraction('pinned'),
    [markFacetInteraction]
  );

  useEffect(() => {
    if (hidden) setOpen(false);
  }, [hidden]);

  const count = getRefinementCount(attribute);
  const title = getFacetTitle(attribute, t);

  return (
    <DropDown.Root open={open} onOpenChange={setOpen} disableSideFlip>
      <DropDown.Trigger
        variant="primary"
        className={cn(styles.pillTrigger, hidden && styles.pillHidden)}
      >
        {title}
        {count > 0 && <span className={styles.pillCount}>{` (${count})`}</span>}
      </DropDown.Trigger>
      <DropDown.Content className={styles.pillPopover} wideScrollGutter>
        <div
          className={styles.pillRefinement}
          onClickCapture={markPinned}
          onChangeCapture={markPinned}
        >
          <FacetRefinement
            attribute={attribute}
            locale={locale}
            onRequestClose={close}
            context="filterBar"
            hasActiveRefinement={count > 0}
          />
        </div>
      </DropDown.Content>
    </DropDown.Root>
  );
};

export default FilterPill;
